import { DataLakeQuerySortDirection, QueryChartType, QueryUserConfig } from 'Generated/schema';
import keyBy from 'lodash/keyBy';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';
import { useGetDatalakeQuery } from 'Pages/Investigate/QueryBuilder/graphql/getDatalakeQuery.generated';
import React from 'react';
import { SearchResultsTableViewEnum } from '../types';
import { useUpdateQueryUserConfig } from './FiltersList/graphql/updateQueryUserConfig.generated';

type UserConfigContextProviderProps = {
  queryId: string;
  /** Must match results table column filtering (e.g. `row_object` omitted in non-compact view). */
  resultsTableView?: SearchResultsTableViewEnum;
  children: React.ReactChild;
};

/** Result grid static columns (Time, Database, Event); visibility toggled locally in v1 (not GraphQL). */
export const STATIC_COLUMN_IDS = ['p_timeline', 'databasename', 'row_object'] as const;

/** Count of visible columns in the results table (static + tableProperties). */
export function getTotalVisibleResultColumnCount(
  hiddenStaticColumns: ReadonlySet<string>,
  tablePropertiesLength: number,
  resultsTableView: SearchResultsTableViewEnum = SearchResultsTableViewEnum.COMPACT,
): number {
  const staticIdsForCount =
    resultsTableView === SearchResultsTableViewEnum.DETAILED
      ? STATIC_COLUMN_IDS.filter(id => id !== 'row_object')
      : STATIC_COLUMN_IDS;
  const staticVisible = staticIdsForCount.filter(id => !hiddenStaticColumns.has(id)).length;
  return staticVisible + tablePropertiesLength;
}

export type UserConfigContextValues = {
  userConfig: QueryUserConfig;
  selectProperty: (id: string) => void;
  unselectProperty: (id: string) => void;
  toggleChartExpand: (id: string) => void;
  isPropertySelected: (id: string) => boolean;
  updatePropertyOrder: (orderedIds: string[]) => void;
  toggleChartSortDirection: (id: string) => void;
  queryLoading: boolean;
  hiddenStaticColumns: ReadonlySet<string>;
  toggleStaticColumn: (columnId: string) => void;
  /** Visible result table columns (static + `tableProperties`); derived once per provider update. */
  totalVisibleColumns: number;
  /** Matches `QueryBuilderResultsTable` view; `row_object` is not a table column in DETAILED mode. */
  resultsTableView: SearchResultsTableViewEnum;
};

const defaultUserConfig: QueryUserConfig = {
  queryId: '',
  chartProperties: [],
  tableProperties: [],
};

export const DefaultUserConfigContextValues = {
  userConfig: defaultUserConfig,
  selectProperty: () => undefined,
  unselectProperty: () => undefined,
  toggleChartExpand: () => undefined,
  isPropertySelected: () => false,
  updatePropertyOrder: () => undefined,
  toggleChartSortDirection: () => undefined,
  queryLoading: false,
  hiddenStaticColumns: new Set<string>(),
  toggleStaticColumn: () => undefined,
  totalVisibleColumns: getTotalVisibleResultColumnCount(new Set<string>(), 0),
  resultsTableView: SearchResultsTableViewEnum.COMPACT,
};

export const UNEXPANDED_CHART_LIMIT = 5;
export const EXPANDED_CHART_LIMIT = 25;

export const UserConfigContext = React.createContext<UserConfigContextValues>(
  DefaultUserConfigContextValues,
);

export function UserConfigContextProvider({
  children,
  queryId,
  resultsTableView = SearchResultsTableViewEnum.COMPACT,
}: UserConfigContextProviderProps) {
  const { data: queryResponse, loading: queryLoading } = useGetDatalakeQuery({
    skip: !queryId,
    variables: {
      id: queryId,
    },
  });

  const userConfig = React.useMemo(
    () => queryResponse?.dataLakeQuery?.userConfig || defaultUserConfig,
    [queryResponse],
  );

  const tablePropsMap = React.useMemo(
    () => keyBy(userConfig.tableProperties, 'propertyId'),
    [userConfig.tableProperties],
  );
  const chartPropsMap = React.useMemo(
    () => keyBy(userConfig.chartProperties, 'propertyId'),
    [userConfig.chartProperties],
  );

  const [updateQueryUserConfig] = useUpdateQueryUserConfig();

  const [hiddenStaticColumns, setHiddenStaticColumns] = React.useState<Set<string>>(
    () => new Set(),
  );

  const tablePropertiesLength = userConfig.tableProperties?.length ?? 0;

  const prevQueryIdRef = React.useRef(queryId);

  // Reset local static-column visibility when switching queries (provider stays mounted). When
  // tableProperties shrink via GraphQL (e.g. to zero) while all static columns were hidden,
  // repair so at least one column remains visible (toggle/unselect guards only apply to user actions).
  React.useEffect(() => {
    const queryChanged = prevQueryIdRef.current !== queryId;
    prevQueryIdRef.current = queryId;

    setHiddenStaticColumns(prev => {
      if (queryChanged) {
        return new Set();
      }
      if (getTotalVisibleResultColumnCount(prev, tablePropertiesLength, resultsTableView) >= 1) {
        return prev;
      }
      return new Set();
    });
  }, [queryId, tablePropertiesLength, resultsTableView]);

  const totalVisibleColumns = React.useMemo(
    () =>
      getTotalVisibleResultColumnCount(
        hiddenStaticColumns,
        tablePropertiesLength,
        resultsTableView,
      ),
    [hiddenStaticColumns, tablePropertiesLength, resultsTableView],
  );

  const toggleStaticColumn = React.useCallback(
    (columnId: string) => {
      setHiddenStaticColumns(prev => {
        const next = new Set(prev);
        const willHide = !next.has(columnId);
        if (willHide) {
          const prospectiveHidden = new Set(next);
          prospectiveHidden.add(columnId);
          const countAfter = getTotalVisibleResultColumnCount(
            prospectiveHidden,
            tablePropertiesLength,
            resultsTableView,
          );
          if (countAfter < 1) {
            return prev;
          }
          next.add(columnId);
        } else {
          next.delete(columnId);
        }
        return next;
      });
    },
    [tablePropertiesLength, resultsTableView],
  );

  const isPropertySelected = React.useMemo(() => {
    return (id: string): boolean => Boolean(tablePropsMap[id]) || Boolean(chartPropsMap[id]);
  }, [tablePropsMap, chartPropsMap]);

  const optimisticallyUpdateConfig = React.useCallback(
    newConfig => {
      updateQueryUserConfig({
        variables: {
          input: {
            queryId: newConfig.queryId,
            chartProperties: newConfig.chartProperties.map(cp =>
              omit(cp, 'propertyPath', 'propertyDisplayName', 'propertyTableName'),
            ),
            tableProperties: newConfig.tableProperties.map(tp =>
              omit(tp, 'propertyPath', 'propertyDisplayName', 'propertyTableName'),
            ),
          },
        },
        optimisticResponse: {
          updateQueryUserConfig: {
            id: queryResponse.dataLakeQuery.id,
            userConfig: newConfig,
          },
        },
      });
    },
    [queryResponse?.dataLakeQuery?.id, updateQueryUserConfig],
  );

  const selectProperty = React.useCallback(
    key => {
      const currentConfig = queryResponse?.dataLakeQuery?.userConfig;
      if (!currentConfig) {
        return;
      }

      optimisticallyUpdateConfig({
        ...currentConfig,
        tableProperties: [
          ...currentConfig.tableProperties,
          {
            propertyId: key,
            order: currentConfig.tableProperties.length,
            propertyPath: '',
            propertyTableName: '',
            propertyDisplayName: '',
          },
        ],
        chartProperties: [
          ...currentConfig.chartProperties,
          {
            propertyId: key,
            limit: 5,
            order: currentConfig.chartProperties.length,
            sortDirection: DataLakeQuerySortDirection.Desc,
            chartType: QueryChartType.HorizontalBar,
            colorHexes: [],
            propertyPath: '',
            propertyTableName: '',
            propertyDisplayName: '',
          },
        ],
      });
    },
    [optimisticallyUpdateConfig, queryResponse?.dataLakeQuery?.userConfig],
  );

  const unselectProperty = React.useCallback(
    key => {
      const currentConfig = queryResponse?.dataLakeQuery?.userConfig;
      if (!currentConfig) {
        return;
      }

      const newTableProperties = currentConfig.tableProperties
        .filter(({ propertyId }) => propertyId !== key)
        .map((tp, idx) => ({ ...tp, order: idx }));

      const newChartProperties = currentConfig.chartProperties
        .filter(({ propertyId }) => propertyId !== key)
        .map((cp, idx) => ({ ...cp, order: idx }));

      const countAfterUnselect = getTotalVisibleResultColumnCount(
        hiddenStaticColumns,
        newTableProperties.length,
        resultsTableView,
      );
      if (countAfterUnselect < 1) {
        return;
      }

      optimisticallyUpdateConfig({
        ...currentConfig,
        tableProperties: newTableProperties,
        chartProperties: newChartProperties,
      });
    },
    [
      optimisticallyUpdateConfig,
      queryResponse?.dataLakeQuery?.userConfig,
      hiddenStaticColumns,
      resultsTableView,
    ],
  );

  const updatePropertyOrder = React.useCallback(
    (orderedIds: string[]) => {
      const currentConfig = queryResponse?.dataLakeQuery?.userConfig;
      if (!currentConfig) {
        return;
      }

      const resortProperties = (allProps: { propertyId: string }[]) =>
        sortBy(allProps, o => orderedIds.indexOf(o.propertyId)).map((props, idx) => ({
          ...props,
          order: idx,
        }));

      const configPatch = {
        tableProperties: resortProperties(currentConfig.tableProperties),
        chartProperties: resortProperties(currentConfig.chartProperties),
      };

      optimisticallyUpdateConfig({
        ...currentConfig,
        ...configPatch,
      });
    },
    [optimisticallyUpdateConfig, queryResponse?.dataLakeQuery?.userConfig],
  );

  const toggleChartExpand = React.useCallback(
    key => {
      const currentConfig = queryResponse?.dataLakeQuery?.userConfig;
      if (!currentConfig) {
        return;
      }

      optimisticallyUpdateConfig({
        ...currentConfig,
        chartProperties: currentConfig.chartProperties.map(cp => {
          if (cp.propertyId !== key) {
            return cp;
          }
          return {
            ...cp,
            limit:
              cp.limit <= UNEXPANDED_CHART_LIMIT ? EXPANDED_CHART_LIMIT : UNEXPANDED_CHART_LIMIT,
          };
        }),
      });
    },
    [optimisticallyUpdateConfig, queryResponse],
  );

  const toggleChartSortDirection = React.useCallback(
    key => {
      const currentConfig = queryResponse?.dataLakeQuery?.userConfig;
      if (!currentConfig) {
        return;
      }

      optimisticallyUpdateConfig({
        ...currentConfig,
        chartProperties: currentConfig.chartProperties.map(cp => {
          if (cp.propertyId !== key) {
            return cp;
          }
          return {
            ...cp,
            sortDirection:
              cp.sortDirection === DataLakeQuerySortDirection.Desc
                ? DataLakeQuerySortDirection.Asc
                : DataLakeQuerySortDirection.Desc,
          };
        }),
      });
    },
    [optimisticallyUpdateConfig, queryResponse],
  );

  const providerState = React.useMemo(
    (): UserConfigContextValues => ({
      userConfig,
      selectProperty,
      unselectProperty,
      toggleChartExpand,
      isPropertySelected,
      updatePropertyOrder,
      toggleChartSortDirection,
      queryLoading,
      hiddenStaticColumns,
      toggleStaticColumn,
      totalVisibleColumns,
      resultsTableView,
    }),
    [
      userConfig,
      selectProperty,
      unselectProperty,
      toggleChartExpand,
      isPropertySelected,
      updatePropertyOrder,
      toggleChartSortDirection,
      queryLoading,
      hiddenStaticColumns,
      toggleStaticColumn,
      totalVisibleColumns,
      resultsTableView,
    ],
  );

  return <UserConfigContext.Provider value={providerState}>{children}</UserConfigContext.Provider>;
}

export function useUserConfigContext() {
  const queryBuilderState = React.useContext<UserConfigContextValues>(UserConfigContext);
  return React.useMemo(() => queryBuilderState, [queryBuilderState]);
}
